import React, { Component } from "react";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import AddCircle from "@mui/icons-material/AddCircle";
import CheckCircle from "@mui/icons-material/CheckCircle";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import client from "common/admin/client";
import { userFullname } from "common/utils";

const permissionExists = (user, permissions) =>
  permissions.filter((p) => p.user_id === user.id).length > 0;

const MANAGE = "manage";

const initialState = {
  error: null,
  addedPermissions: [],
  users: [],
  errorMessage: "",
};

export default class FormDialog extends Component {
  static propTypes = {
    open: PropTypes.bool,
    resourceId: PropTypes.number.isRequired,
    resourceType: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    initialPermissions: PropTypes.arrayOf(
      PropTypes.shape({
        user_id: PropTypes.number.isRequired,
      }),
    ),
  };

  static defaultProps = {
    open: false,
    initialPermissions: [],
  };

  state = initialState;

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.resourceId !== prevProps.resourceId ||
      this.props.resourceType !== prevProps.resourceType
    ) {
      this.setState(initialState);
    }
  }

  handleClose = () => {
    this.setState(initialState, this.props.onClose);
  };

  handleSearchFilterChange = async (text) => {
    if (!text) {
      this.setState({ users: [], error: null });
      return;
    }

    const query = {
      email_like: text,
      access_level: "admin",
    };

    try {
      const users = await client.get("users", { query });
      this.setState({ users, error: null });
    } catch (error) {
      this.setState({
        error,
        errorMessage: "Failed to load search results",
      });
    }
  };

  handleCreate = async (userId) => {
    const { resourceId, resourceType } = this.props;

    const body = {
      user_id: userId,
      resource: resourceType,
      resource_id: resourceId,
      action: MANAGE,
    };
    try {
      const permission = await client.post("permissions", { body });
      this.setState((state) => ({
        ...state,
        addedPermissions: state.addedPermissions.concat(permission),
      }));
    } catch (error) {
      this.setState({
        error,
        errorMessage: "Failed to create permission",
      });
    }
  };

  render() {
    const { open, initialPermissions } = this.props;
    const { users, addedPermissions, errorMessage } = this.state;
    const mergedPermissions = initialPermissions.concat(addedPermissions);
    const debouncedSearchFilter = debounce(this.handleSearchFilterChange, 500);

    const handleSearchFilterChange = (e) =>
      debouncedSearchFilter(e.target.value);

    return (
      <Dialog open={open} onClose={this.handleClose}>
        <DialogTitle>Add Administrators</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Search for a user by email address to add them as an adminstrator.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Email Address"
            type="email"
            fullWidth
            onChange={handleSearchFilterChange}
          />
          {users.length ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Allow Access</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{userFullname(user)}</TableCell>
                    <TableCell>
                      {permissionExists(user, mergedPermissions) ? (
                        <IconButton disabled>
                          <CheckCircle />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => this.handleCreate(user.id)}
                          color="primary"
                        >
                          <AddCircle />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
          {errorMessage ? (
            <Typography variant="caption" color="error">
              {errorMessage}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
